import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, ButtonGroup } from "reactstrap";
import { useTranslation } from "react-i18next";
import { get } from "lodash";
import languages from "../../common/languages";

const LOCALES = ["pt-BR", "en", "es"] as const;
export type ProductLocale = (typeof LOCALES)[number];

export function normalizeLocale(
  raw: string | null | undefined
): ProductLocale | null {
  if (!raw) return null;
  const n = raw.replace("_", "-").toLowerCase();
  if (n === "pt" || n === "pt-br") return "pt-BR";
  if (n === "en") return "en";
  if (n === "es") return "es";
  return null;
}

/** Preserva lang/market/ref ao ir de login ↔ register. */
export function authSiblingPath(path: "/login" | "/register"): string {
  const params = new URLSearchParams(window.location.search);
  const keep = new URLSearchParams();
  (["lang", "market", "ref"] as const).forEach((key) => {
    const v = params.get(key);
    if (v) keep.set(key, v);
  });
  const q = keep.toString();
  return q ? `${path}?${q}` : path;
}

function localeLabelKey(locale: ProductLocale): string {
  if (locale === "pt-BR") return "auth.localeBar.lang.ptBR";
  if (locale === "es") return "auth.localeBar.lang.es";
  return "auth.localeBar.lang.en";
}

type Props = {
  /** Locale controlado (Register) — se omitido, só mexe no i18n (Login). */
  value?: string;
  onChange?: (locale: ProductLocale) => void;
};

/**
 * Seletor de idioma nas telas públicas de auth (sem header Velzon).
 * Por quê: quem veio do site com ?lang= precisa ver e mudar o idioma sem caçar dropdown escondido.
 */
const AuthLocaleBar = ({ value, onChange }: Props) => {
  const { t, i18n } = useTranslation();
  const fromSite = useMemo(
    () =>
      normalizeLocale(new URLSearchParams(window.location.search).get("lang")),
    []
  );
  const [hintVisible, setHintVisible] = useState(!!fromSite);

  const current: ProductLocale = useMemo(() => {
    const fromValue = normalizeLocale(value || null);
    if (fromValue) return fromValue;
    return normalizeLocale(i18n.language) || "pt-BR";
  }, [value, i18n.language]);

  useEffect(() => {
    if (!fromSite) return;
    if (fromSite !== i18n.language) {
      void i18n.changeLanguage(fromSite);
      localStorage.setItem("I18N_LANGUAGE", fromSite);
    }
    onChange?.(fromSite);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyLocale = (locale: ProductLocale) => {
    localStorage.setItem("I18N_LANGUAGE", locale);
    void i18n.changeLanguage(locale);
    onChange?.(locale);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("lang", locale);
      window.history.replaceState({}, "", url.toString());
    } catch {
      /* ignore */
    }
    setHintVisible(false);
  };

  return (
    <div className="auth-locale-bar mb-3">
      {hintVisible && fromSite ? (
        <Alert
          color="info"
          className="py-2 px-3 mb-2 text-start"
          toggle={() => setHintVisible(false)}
        >
          {t("auth.localeBar.fromSite", {
            lang: t(localeLabelKey(fromSite)),
          })}
        </Alert>
      ) : null}

      <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2">
        <span className="text-muted small mb-0">
          {t("auth.localeBar.label")}
        </span>
        <ButtonGroup size="sm" aria-label={t("auth.localeBar.label")}>
          {LOCALES.map((code) => (
            <Button
              key={code}
              type="button"
              color={current === code ? "primary" : "light"}
              outline={current !== code}
              onClick={() => applyLocale(code)}
              aria-pressed={current === code}
            >
              <img
                src={get(languages, `${code}.flag`)}
                alt=""
                height="14"
                className="rounded me-1"
              />
              {t(localeLabelKey(code))}
            </Button>
          ))}
        </ButtonGroup>
      </div>
    </div>
  );
};

export default AuthLocaleBar;
